import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { makeStyles } from '@mui/styles';
import { Paper, Grid, Box, Button, useMediaQuery, Avatar, Typography, Link, Tooltip, IconButton } from '@mui/material';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';
import { useAuthStore } from '../store/authStore';
import * as MyCollections from "../services/MyCollections";
import { urlClientSurvey } from '../config';
import { useThemeContext } from '../components/contexts/ThemeContext';
import CustomTooltip from '../components/CustomTooltip';
import Controls from '../components/controls/Controls';

const useStyles = makeStyles(() => ({
    pageContent: {
        marginTop: 20,
    },
}));

const baseCellStyle = (darkTheme) => ({
    color: darkTheme ? "#E0E0E0" : "#212121",
    fontWeight: 500,
    display: 'flex',
    lineHeight: '1.5',
    justifyContent: 'center',
    textAlign: 'left',
    alignItems: 'center',
    whiteSpace: 'normal',
    overflow: 'hidden',
});

const highlightCellStyle = (darkTheme) => ({
    backgroundColor: darkTheme ? "#741b47" : "#feb33c",
    color: darkTheme ? "#FAFAFA" : "#212121",
    fontWeight: 600,
    textAlign: 'left'
});

const accountantCellStyle = (darkTheme) => ({
    color: darkTheme ? "#ECEFF1" : "#741b47",
    fontWeight: 500,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const idCellStyle = (darkTheme) => ({
    color: darkTheme ? '#009cdc' : '#255c94',
    fontWeight: 500,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const satisfactionCellStyle = (darkTheme, value) => {
    let color = darkTheme ? "#E0E0E0" : "#212121"; // domyślny
    if (value >= 7) {
        color = "#2E7D32"; // zielony
    } else if (value >= 4) {
        color = "#ED6C02"; // pomarańczowy
    } else {
        color = "#C62828"; // czerwony
    }

    return {
        color,
        fontWeight: 700,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };
};

const ClientCellRendererClientSurvey = (props) => {
  const { data, darkTheme } = props;
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedNip, setCopiedNip] = useState(false);

  if (!data) return null;

  const companyName = data.client || '';
  const nip = data.nip || null;
  const email = data.clientEmail || null;
  const rawRep = data.representative;
  const representative = rawRep && rawRep !== 'null null' && rawRep !== '-' ? rawRep : null;

  const getInitials = (name) => {
    if (!name) return 'K';
    const words = name.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleCopy = (e, text, setCopiedFn) => {
    e.stopPropagation();
    if (text) {
      navigator.clipboard.writeText(text);
      setCopiedFn(true);
      setTimeout(() => setCopiedFn(false), 2000);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 1,
        px: 0.5,
        gap: 0.75,
        lineHeight: 1.3,
      }}
    >
      {/* Row 1: Avatar + Company Name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.75rem',
            fontWeight: 700,
            bgcolor: darkTheme ? 'rgba(56, 189, 248, 0.15)' : 'rgba(37, 99, 235, 0.1)',
            color: darkTheme ? '#38bdf8' : '#2563eb',
            border: `1px solid ${darkTheme ? 'rgba(56, 189, 248, 0.3)' : 'rgba(37, 99, 235, 0.25)'}`,
            flexShrink: 0,
            boxShadow: darkTheme ? '0 2px 4px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          {getInitials(companyName)}
        </Avatar>

        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: '0.875rem',
            color: darkTheme ? '#60a5fa' : '#1d4ed8',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {companyName}
        </Typography>
      </Box>

      {/* Row 2: Representative, NIP & Email Details */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.4,
          pl: 1,
          borderLeft: `2px solid ${darkTheme ? 'rgba(148, 163, 184, 0.25)' : 'rgba(203, 213, 225, 0.7)'}`,
          ml: 0.75,
        }}
      >
        {/* NIP */}
        {nip && nip !== '-' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <BadgeIcon sx={{ fontSize: 14, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                color: darkTheme ? '#cbd5e1' : '#334155',
                fontFamily: 'monospace',
                fontWeight: 600,
              }}
            >
              NIP: {nip}
            </Typography>
            <Tooltip title={copiedNip ? "Skopiowano!" : "Kopiuj NIP"} arrow placement="right">
              <IconButton
                size="small"
                onClick={(e) => handleCopy(e, nip, setCopiedNip)}
                sx={{
                  p: 0.2,
                  ml: 0.2,
                  color: copiedNip ? (darkTheme ? '#4ade80' : '#16a34a') : (darkTheme ? '#64748b' : '#94a3b8'),
                  '&:hover': {
                    color: darkTheme ? '#cbd5e1' : '#475569',
                    backgroundColor: darkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                {copiedNip ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Representative */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <PersonOutlinedIcon sx={{ fontSize: 14, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              color: representative ? (darkTheme ? '#e2e8f0' : '#334155') : (darkTheme ? '#64748b' : '#94a3b8'),
              fontStyle: representative ? 'normal' : 'italic',
              fontWeight: representative ? 500 : 400,
            }}
          >
            {representative || 'Brak reprezentanta'}
          </Typography>
        </Box>

        {/* Email */}
        {email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <MailOutlineIcon sx={{ fontSize: 14, color: darkTheme ? '#94a3b8' : '#64748b', flexShrink: 0 }} />
            <Link
              underline="hover"
              href={`mailto:${email}`}
              onClick={(e) => e.stopPropagation()}
              sx={{
                fontSize: '0.75rem',
                color: darkTheme ? '#38bdf8' : '#0284c7',
                fontWeight: 500,
                maxWidth: '180px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': { color: darkTheme ? '#7dd3fc' : '#0369a1' }
              }}
            >
              {email}
            </Link>
            <Tooltip title={copiedEmail ? "Skopiowano!" : "Kopiuj email"} arrow placement="right">
              <IconButton
                size="small"
                onClick={(e) => handleCopy(e, email, setCopiedEmail)}
                sx={{
                  p: 0.2,
                  ml: 0.2,
                  color: copiedEmail ? (darkTheme ? '#4ade80' : '#16a34a') : (darkTheme ? '#64748b' : '#94a3b8'),
                  '&:hover': {
                    color: darkTheme ? '#cbd5e1' : '#475569',
                    backgroundColor: darkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                {copiedEmail ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

function ClientSurvey() {
    const { token: token } = useAuthStore();

    const [gridApi, setGridApi] = useState(null);
    const [tableData, setTableData] = useState([]);
    const classes = useStyles();
    const { mode, toggleTheme } = useThemeContext();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const darkTheme = mode === 'dark';
    const gridRef = useRef();

    // Each Column Definition results in one Column.
    const columnDefs = useMemo(() => [
        {
            headerName: "Klient",
            field: "client",
            lockPosition: "left",
            autoHeight: true,
            minWidth: 230,
            cellStyle: { textAlign: 'left' },
            cellRenderer: (params) => (
                <ClientCellRendererClientSurvey
                    data={params.data}
                    darkTheme={darkTheme}
                />
            )
        },
        {
            headerName: "Księgowy",
            field: "accountant",
            lockPosition: "left",
            cellStyle: accountantCellStyle(darkTheme),
        },
        {
            headerName: "Stopień zadowolenia",
            field: "satisfaction",
            filter: "agNumberColumnFilter",
            lockPosition: "left",
            valueGetter: (params) => {
                return params.data.satisfaction ? parseInt(params.data.satisfaction, 10) : null;
            },
            cellStyle: (params) => satisfactionCellStyle(darkTheme, params.value),
        },
        {
            headerName: "Co można poprawić?",
            field: "improve",
            lockPosition: "left",
            cellStyle: baseCellStyle(darkTheme),
            valueFormatter: (params) => {
                if (params.value) {
                    const maxLength = 70;
                    return params.value.length > maxLength
                        ? params.value.substring(0, maxLength) + "..."
                        : params.value;
                }
                return 'brak...';
            },
            tooltipValueGetter: (params) => params.value || '',
        },
        {
            headerName: "Identyfikator ankiety",
            field: "surveyId",
            lockPosition: "left",
            cellStyle: idCellStyle(darkTheme),
        }
    ], [darkTheme]);


    useEffect(() => {
        fetchElements();
    }, []);

    const onGridReady = (params) => {
        setGridApi(params)
    }

    const defaultColDef = {
        resizable: true,
        editable: false,
        floatingFilter: true,
        filter: true,
        sortable: true,
        autoHeight: true,
        flex: 1,
        tooltipComponent: CustomTooltip,
    }

    const fetchElements = async () => {
        let url = urlClientSurvey + '/all';
        fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(response => response.json())
            .then(data => {
                const reversedData = data;
                setTableData(reversedData);
            })
            .catch(error => console.error(error));
    };

    const clearFilters = useCallback(() => {
        const api = gridRef.current?.api;
        const columnApi = gridRef.current?.columnApi;

        if (api) {
            api.setFilterModel(null);

            if (api.applyColumnState) {
                api.applyColumnState({
                    defaultState: { sort: null },
                });
            } else if (columnApi && columnApi.applyColumnState) {
                columnApi.applyColumnState({
                    defaultState: { sort: null },
                });
            } else if (api.setSortModel) {
                api.setSortModel(null);
            }
        }
    }, []);

    return (
        <>
            <Paper className={classes.pageContent} elevation={0}>
                <Grid container justifyContent="flex-end" style={{ gap: '15px' }}>
                    <Grid item xs={12} sm={8} md={12}>
                <Box display="flex" justifyContent="flex-end" flexWrap="wrap" gap="12px" sx={{ px: 2, pt: 2 }}>
                    <Controls.PremiumButton
                        variant="glass"
                        label="Wyczyść"
                        onClick={clearFilters}
                        icon="fas fa-filter"
                    />
                </Box>
                    </Grid>
                </Grid>
                <Box sx={{ m: '20px' }} />
                <Grid container rowSpacing={1} spacing={{ xs: 2, md: 4 }} columns={{ xs: 2, sm: 6, md: 12 }}>
                    <Grid item xs={12}>
                        <div className="App">
                            <div className="tableData">
                                {/* On div wrapping Grid a) specify theme CSS Class Class and b) sets Grid size */}
                                <div
                                    className={darkTheme ? 'ag-theme-balham-dark' : 'ag-theme-balham'}
                                    style={{ marginLeft: 0, marginRight: 0, height: 650 }}
                                >
                                    <AgGridReact
                                        key={darkTheme ? 'grid-dark' : 'grid-light'}
                                        rowData={tableData}
                                        ref={gridRef}
                                        columnDefs={columnDefs}
                                        defaultColDef={defaultColDef}
                                        onGridReady={onGridReady}
                                        suppressBrowserResizeObserver={true}
                                        animateRows={true}
                                        rowSelection={false}
                                        suppressSizeToFit={true}
                                        localeText={MyCollections.localeTextPL}
                                        tooltipShowDelay={0}
                                        popupParent={document.body}
                                        headerHeight={45}
                                        overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                                        overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Ładowanie danych...</span>'}
                                    />
                                </div>
                            </div>
                        </div>
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
}

export default ClientSurvey;